/**
 * Forms sent about one record, with their answers. Record-type agnostic:
 * drop it on any record page that can send forms.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, ClipboardList, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useOrg } from "@/admin/hooks/use-org";
import {
  useCancelForm,
  useRecordForms,
  useResendForm,
  type FormRequest,
  type FormRequestStatus,
} from "@/admin/hooks/use-forms";
import { AttributeFileGallery } from "@/shared/components/evidence/attribute-file-gallery";
import { isFileAttributeFieldType } from "@/shared/components/evidence/attribute-file-gallery-utils";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { toastApiError } from "@/shared/lib/api-error";
import { formatDisplayValue, normaliseRegistrationField } from "@/shared/lib/forms";
import { useFormatters, useFormattingContext } from "@/shared/lib/formatting";

const STATUS_VARIANT: Record<FormRequestStatus, "secondary" | "outline" | "default"> = {
  sent: "outline",
  submitted: "default",
  expired: "secondary",
  cancelled: "secondary",
};

function Answers({ form }: { form: FormRequest }) {
  const { t } = useTranslation();
  const { locale, currency } = useFormattingContext();
  const answered = form.questions.filter((q) => {
    const value = form.answers[q.code_name];
    return value !== undefined && value !== null && value !== "";
  });
  if (answered.length === 0) {
    return (
      <p className="text-[12px] text-[var(--ink-3)]">
        {t("forms.record.noAnswers", "No answers yet.")}
      </p>
    );
  }
  return (
    <dl className="grid gap-2" data-testid="record-form-answers">
      {answered.map((q) => {
        const value = form.answers[q.code_name];
        const isFile = isFileAttributeFieldType(q.field_type);
        return (
          <div key={q.code_name} className={isFile ? "flex flex-col gap-1.5" : "flex gap-3"}>
            <dt className="w-[150px] shrink-0 text-[12px] text-[var(--ink-3)]">
              {q.display_name}
            </dt>
            <dd className="min-w-0 text-[12px] text-[var(--ink)]">
              {isFile ? (
                <AttributeFileGallery value={value} />
              ) : (
                formatDisplayValue(normaliseRegistrationField(q), value, { locale, currency })
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function FormRow({ form }: { form: FormRequest }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  // Submitted forms open by default; follows a cached row that turns submitted.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? form.status === "submitted";
  const resend = useResendForm();
  const cancel = useCancelForm();
  const busy = resend.isPending || cancel.isPending;
  const statusLabel: Record<FormRequestStatus, string> = {
    sent: t("forms.record.status.sent", "Sent"),
    submitted: t("forms.record.status.submitted", "Submitted"),
    expired: t("forms.record.status.expired", "Link expired"),
    cancelled: t("forms.record.status.cancelled", "Cancelled"),
  };
  const when = form.submitted_at ?? form.cancelled_at ?? form.sent_at;

  const run = async (action: "resend" | "cancel") => {
    try {
      if (action === "resend") {
        await resend.mutateAsync(form.uuid);
        toast.success(t("forms.record.resent", "A new link was sent."));
      } else {
        await cancel.mutateAsync(form.uuid);
        toast.success(t("forms.record.cancelledToast", "Form cancelled. The link no longer works."));
      }
    } catch (err) {
      toastApiError(err, t("forms.record.actionFailed", "Could not update the form."));
    }
  };

  return (
    <li className="border-b-[0.5px] border-b-[var(--rule)] last:border-b-0" data-testid="record-form">
      <Button
        variant="ghost"
        onClick={() => setToggled(!open)}
        className="flex h-auto w-full items-center gap-2 rounded-none px-4 py-3 text-left font-light"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] text-[var(--ink)]">{form.template_name}</span>
          <span className="block truncate text-[11px] text-[var(--ink-3)]">
            {form.recipient_name || form.recipient_email} · {formatDateTime(when)}
          </span>
        </span>
        <Badge variant={STATUS_VARIANT[form.status]}>{statusLabel[form.status]}</Badge>
      </Button>
      {open ? (
        <div className="flex flex-col gap-3 px-4 pb-4 pl-10">
          <Answers form={form} />
          {form.status === "sent" || form.status === "expired" ? (
            <div className="flex gap-2">
              <Button variant="outline" size="xs" disabled={busy} onClick={() => run("resend")}>
                {t("forms.record.resend", "Send a new link")}
              </Button>
              {form.status === "sent" ? (
                <Button variant="ghost" size="xs" disabled={busy} onClick={() => run("cancel")}>
                  {t("forms.record.cancel", "Cancel form")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function RecordForms({
  recordType,
  recordUuid,
}: {
  recordType: string;
  recordUuid: string;
}) {
  const { t } = useTranslation();
  const { isPartnerAccess } = useOrg();
  const { data, isPending, isError, refetch } = useRecordForms(recordType, recordUuid);

  if (isPartnerAccess) return null;
  if (isPending) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-[12px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        {t("forms.record.loading", "Loading forms...")}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 text-[12px] text-[var(--ink-2)]">
        {t("forms.record.error", "Couldn't load forms.")}
        <Button variant="outline" size="xs" onClick={() => refetch()}>
          {t("common.retry", "Retry")}
        </Button>
      </div>
    );
  }
  // Records with no forms show nothing: most never get one.
  if (!data || data.length === 0) return null;

  return (
    <section className="border-t-[0.5px] border-t-[var(--rule)]" data-testid="record-forms">
      <h3 className="flex items-center gap-1.5 px-4 pb-1 pt-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
        <ClipboardList className="h-3 w-3" />
        {t("forms.record.title", "Forms")}
      </h3>
      <ul>
        {data.map((form) => (
          <FormRow key={form.uuid} form={form} />
        ))}
      </ul>
    </section>
  );
}
